import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQS = [
  {
    question: 'How does Sortio handle CAD drawing files with discipline prefixes like AR, ST, PW, and TR?',
    answer:
      "Sortio reads each drawing's filename and content to detect discipline codes such as AR (architectural), ST (structural), PW (plumbing/wet services), and TR (telecom). It then routes each file to the matching discipline subfolder under your project root. You can configure the exact prefix list in the AI Rule Builder so Sortio matches your firm's naming standard, including custom codes like ME (mechanical) or EL (electrical).",
  },
  {
    question: 'Can Sortio manage drawing revision history like V1, V2, and V3?',
    answer:
      "Yes. Sortio detects revision markers (V1, V2, V3, Rev01, Rev02, R01, etc.) in filenames and keeps every revision in a versioned subfolder so a newer revision never silently overwrites the prior one. You see a side by side preview before anything moves. Sortio also pairs DWG and PDF revisions of the same drawing so the two formats stay together when versions roll forward.",
  },
  {
    question: 'How does Sortio organize change requests (CRFs) and RFIs?',
    answer:
      "Sortio recognizes change request and RFI filename patterns such as CRF-Change_Request_form-0019 or RFI-2026-014, groups them by request number, and files them into a CRF or RFI subfolder. Each request number becomes its own folder so initial submission, response, revised submission, and final approval stay together as a single thread, instead of scattering across the project root.",
  },
  {
    question: 'Can Sortio organize Bill of Quantities (BOQ) files?',
    answer:
      "Yes. Sortio identifies BOQ workbooks (typically Excel or PDF) by filename and content, and files them by project, phase, and date. If you maintain priced versus unpriced BOQs, you can set a rule in the AI Rule Builder to route those into separate subfolders. Sortio keeps the latest BOQ at the top and archives prior versions so the audit trail is intact.",
  },
  {
    question: 'How does Sortio handle Meeting Minutes (MOM) per project?',
    answer:
      "Sortio detects MOM documents by filename patterns and document content (attendees, agenda, action items) and files them under each project in a Meeting Minutes subfolder, ordered by date. You can configure a rule to route MOMs by meeting type, for example, kickoff, weekly progress, technical coordination, or closeout, so the right minutes are easy to find during handoff.",
  },
  {
    question: 'Does Sortio work with bilingual filenames, including Arabic and English?',
    answer:
      "Yes. Sortio's AI is language agnostic. It handles bilingual filenames (Arabic and English, French and English, and other pairs) correctly, including right to left scripts, and preserves the original filename casing and characters unless you ask it to rename. This is important for ministry, government, and international infrastructure projects where bilingual filing is a contractual requirement.",
  },
  {
    question: 'Does Sortio handle DWG and PDF dual format drawings?',
    answer:
      "Yes. Most engineering teams archive both a native DWG and a PDF plot of every drawing. Sortio detects these pairs by matching the base name and routes them together to the same discipline and revision folder, so the DWG and PDF for drawing AR-101 V2 always stay alongside each other. You never lose the link between the editable file and the issued plot.",
  },
  {
    question: 'Can Sortio handle a 7,000 file or 10,000 file project archive?',
    answer:
      "Yes, but with the right tool inside Sortio. The AI sort works best on runs of up to 5,000 files. For larger archives (7,000 to 10,000+ files), Sortio includes an AI Rule Builder that lets you generate deterministic rules from a small sample, then apply those rules instantly to any number of files. Many engineering teams use a hybrid approach: AI sort the first batch, then the Rule Builder for the rest of the archive.",
  },
  {
    question: 'Does Sortio work for telecom and infrastructure archiving projects?',
    answer:
      "Yes. Telecom site files, infrastructure schematics, AutoCAD plot logs, contractor submittals, ministry correspondence, and project photos are all standard input for Sortio. The on device AI means client and ministry confidentiality is preserved, and the Rule Builder can encode telecom specific taxonomies like site code, region, equipment vendor, and project phase.",
  },
  {
    question: 'How does Sortio handle subcontractor deliverables?',
    answer:
      "When a subcontractor zips up a delivery (drawings, submittals, photos, BOQ updates), drop the unzipped folder into Sortio's watch folder. Sortio identifies each file's type and discipline and merges the delivery into the main project archive in the right subfolders. The subcontractor's original folder structure is preserved as a backup in case you ever need to trace what they actually sent.",
  },
  {
    question: 'Can Sortio maintain an owner, client, and contractor folder triad?',
    answer:
      "Yes. Many engineering archives follow a three party folder structure: Owner correspondence, Client deliverables, and Contractor submittals. Configure the triad once in the AI Rule Builder and Sortio routes each new file into the right party folder based on the sender, recipient, or file metadata. Internal drafts can be routed to a fourth working folder so they never get mixed with issued documents.",
  },
  {
    question: 'Does Sortio integrate with AutoCAD, Revit, or BIM 360?',
    answer:
      "Sortio does not have plugins for AutoCAD, Revit, or BIM 360. Instead, Sortio is filesystem transparent: it reads and writes plain files in whatever folders your team already uses, including folders that are synced to a BIM platform, a network drive, or a local CAD project structure. Your existing CAD workflow is unchanged, Sortio just keeps the underlying files organized.",
  },
  {
    question: 'Is the AI processing safe for confidential client and ministry project IP?',
    answer:
      "Sortio reads files on your Mac. By default it uses our cloud (OpenAI) to plan the sort, sending filenames and the entities it extracts, with file contents sent only if you enable a content-based feature. For confidential client and ministry IP, choose a local LLM (fully offline, no outbound network calls) or BYOK with your firm's own OpenAI, Anthropic, or Azure key (so the only data processing agreement that applies is the one your firm already signed). With a local LLM or BYOK there is no Sortio side cloud step where drawings or BOQs pass through a third party.",
  },
  {
    question: 'How much does Sortio cost for an engineering project manager?',
    answer:
      "Pro is $14.99/month or $99/year and covers a monthly AI allowance sized for heavy use, AI sort up to 5,000 files per run, the AI Rule Builder for large archives, auto sort on file change, BYOK that never counts against your Sortio allowance, and unlimited renaming. For firms standardizing across multiple project teams, Team is $29 per seat per month and adds shared rules, admin console, and centralized AI policy.",
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-4 transition-all duration-300 transform hover:translate-x-1">
      <div className={`apple-panel transition-all duration-300 ${isOpen ? 'ring-1 ring-sortio-primary/30' : ''}`}>
        <button
          className="flex justify-between items-center w-full p-5 text-left rounded-apple focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="text-base font-medium text-apple-50">{question}</span>
          <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-300 ${isOpen ? 'bg-sortio-primary/20' : 'bg-apple-600/40'}`}>
            {isOpen ?
              <ChevronUp className="w-3.5 h-3.5 text-sortio-primary transition-transform duration-300" /> :
              <ChevronDown className="w-3.5 h-3.5 text-apple-300 transition-transform duration-300" />}
          </div>
        </button>
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="p-5 pt-0">
            <p className="text-apple-300 text-sm leading-relaxed">{answer}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const EngineeringFAQ = () => {
  return (
    <section id="faq" className="my-20">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col items-center mb-12">
        <div className="mb-4 rounded-full bg-apple-700/60 backdrop-blur-sm p-2 border border-apple-600/20">
          <HelpCircle className="w-6 h-6 text-sortio-primary" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-semibold text-center tracking-tight text-apple-50">
          Engineering FAQ
        </h2>
        <p className="mt-4 text-apple-300 max-w-2xl text-center font-light">
          Questions we hear from AEC project managers, BIM coordinators, design firm admins,
          and telecom and infrastructure PMs.
        </p>
      </div>

      <div className="max-w-3xl mx-auto space-y-4">
        {FAQS.map((faq, index) => (
          <FAQItem key={index} question={faq.question} answer={faq.answer} />
        ))}
      </div>

      <div className="mt-12 max-w-3xl mx-auto apple-panel p-6 rounded-apple overflow-hidden relative">
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-sortio-primary/20 rounded-full blur-3xl"></div>
        <div className="relative flex flex-col md:flex-row items-center justify-between gap-6 z-10">
          <div>
            <h3 className="text-xl font-medium text-apple-50">Still have questions?</h3>
            <p className="text-apple-300 mt-1">We'll walk through your specific project archive on a demo call.</p>
          </div>
          <a href="#contact" className="apple-button whitespace-nowrap">Contact us</a>
        </div>
      </div>
    </section>
  );
};

export default EngineeringFAQ;
